import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  useGetApiProgramsDeliverableTypesQuery,
  usePostApiProjectsByIdDeliverableTypesMutation,
} from '../../../app/athenaApiGen';

/**
 * AddDeliverableTypesDialog — picker for associating existing deliverable
 * types from the catalog with a project. The BE endpoint
 * (POST /api/projects/{id}/deliverable-types) only accepts type IDs
 * already in the catalog, so this is an "associate" flow, not a
 * "create new type" flow.
 *
 * Types already on the project are filtered out of the picker so users
 * don't double-add. Selections are reset whenever the dialog opens.
 */
export default function AddDeliverableTypesDialog({
  open,
  onClose,
  projectId,
  programId,
  existingTypeIds,
  onAdded,
}) {
  const { data: availableTypes = [], isFetching } =
    useGetApiProgramsDeliverableTypesQuery(
      { programId },
      { skip: !open || !programId },
    );

  const [addTypes, { isLoading: isAdding }] =
    usePostApiProjectsByIdDeliverableTypesMutation();

  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [search, setSearch] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  // Reset selection + search whenever the dialog opens — a stale selection
  // from a previous project would otherwise carry over.
  React.useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch('');
      setErrorMsg('');
    }
  }, [open]);

  // Filter the catalog: drop archived, drop already-associated, then
  // optionally filter by search across title / abbreviation / description.
  const existingSet = React.useMemo(
    () => new Set(existingTypeIds || []),
    [existingTypeIds],
  );
  const pickable = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableTypes
      .filter(
        (t) =>
          !t.archived && !t.deliverableType?.archived && t.deliverableTypeId,
      )
      .filter((t) => !existingSet.has(t.deliverableTypeId))
      .filter((t) => {
        if (!q) return true;
        const title = t.deliverableType?.title || '';
        const abbr = t.deliverableType?.abbreviation || '';
        const desc = t.deliverableType?.description || '';
        return (
          title.toLowerCase().includes(q) ||
          abbr.toLowerCase().includes(q) ||
          desc.toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        (a.deliverableType?.title || a.deliverableType?.abbreviation || '').localeCompare(
          b.deliverableType?.title || b.deliverableType?.abbreviation || '',
          undefined,
          { sensitivity: 'base', numeric: true },
        ),
      );
  }, [availableTypes, existingSet, search]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0 || !projectId) return;
    setErrorMsg('');
    try {
      await addTypes({
        id: projectId,
        handlersProjectDeliverableTypesRequest: {
          deliverableTypeIds: Array.from(selectedIds),
        },
      }).unwrap();
      if (onAdded) onAdded();
      onClose();
    } catch (err) {
      const status = err?.status;
      const message =
        err?.data?.details ||
        err?.data?.message ||
        (status === 403
          ? "You don't have permission to add deliverable types to this project."
          : 'Failed to add deliverable types. Please try again.');
      setErrorMsg(message);
      console.error('[AddDeliverableTypesDialog] add failed', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add deliverable types</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select existing deliverable types to associate with this project.
          New type definitions are managed in the admin catalog.
        </Typography>

        <TextField
          size="small"
          placeholder="Filter by name, abbreviation, or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 1.5 }}
        />

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : pickable.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', py: 2 }}
          >
            {search
              ? 'No catalog entries match your filter.'
              : 'No catalog entries available to add. Either all types are already associated, or the catalog is empty.'}
          </Typography>
        ) : (
          <Box
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              bgcolor: 'background.default',
              p: 1,
              borderRadius: 1,
            }}
          >
            {pickable.map((t) => (
              <FormControlLabel
                key={t.deliverableTypeId}
                sx={{ display: 'flex', alignItems: 'flex-start', ml: -1, py: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(t.deliverableTypeId)}
                    onChange={() => toggleId(t.deliverableTypeId)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t.deliverableType?.abbreviation && (
                        <Box
                          component="span"
                          sx={{ color: 'text.secondary', mr: 0.75 }}
                        >
                          {t.deliverableType.abbreviation}
                        </Box>
                      )}
                      {t.deliverableType?.title || 'Untitled'}
                    </Typography>
                    {t.deliverableType?.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {t.deliverableType.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </Box>
        )}

        {errorMsg && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1.5 }}>
            {errorMsg}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isAdding}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={selectedIds.size === 0 || isAdding}
          sx={{
            background: '#1F4D83',
            '&:hover': { background: '#163966' },
          }}
        >
          {isAdding
            ? 'Adding…'
            : `Add${
                selectedIds.size > 0 ? ` (${selectedIds.size})` : ''
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddDeliverableTypesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  programId: PropTypes.string,
  existingTypeIds: PropTypes.arrayOf(PropTypes.string),
  onAdded: PropTypes.func,
};
